import React, { useCallback, useState } from 'react';
import { Panel} from 'rsuite';
import { isMobile } from 'react-device-detect';
import { Link } from 'react-router-dom';

import NavigationBar from '../Shared/NavigationBar';
import RulesDisclaimer from '../Shared/RulesDisclaimer';
import useScrollToTop from '../../helpers/useScrollToTop';
import RulesSidebarNav from './RulesSidebarNav';
import { COMBAT_STEPS } from '../../helpers/rulesData';
import { usePreferences } from '../Preferences/PreferencesContext';
import { getThemeColors } from '../../helpers/themeColors';

const SPACING = {
  containerMaxWidth: 1100,
  containerPadding: 24,
  containerPaddingMobile: 12,
  headerMarginTop: 24,
  headerMarginBottom: 24,
  titleMarginBottom: 8,
  sectionMarginBottom: 32,
  panelMarginBottom: 16,
  exampleMarginBottom: 12,
  examplePadding: 16,
  exampleBorderRadius: 6,
  exampleBorderLeft: 3,
  sidebarWidth: 220,
  sidebarGap: 32,
  sidebarPadding: 16,
  sidebarBorderRadius: 6,
  sidebarTitleMarginBottom: 8,
  sidebarItemPadding: '6px 0',
  stickyTop: 20,
  backLinkMarginBottom: 16,
  backLinkPadding: '10px 0',
  diagramPadding: 24,
  diagramBorderRadius: 8,
  diagramItemPadding: '10px 16px',
  diagramItemBorderRadius: 6,
  diagramItemMarginBottom: 4,
  stepNumberSize: 36,
  stepNumberBorderRadius: 8,
  stepNumberMarginRight: 16,
  introPadding: 20,
  introBorderRadius: 8,
  cardLinkPadding: '1px 4px',
  cardLinkBorderRadius: 3,
  exampleLabelMarginBottom: 6,
  exampleScenarioMarginBottom: 8,
  paragraphMarginBottom: 12,
  keyRulePadding: '6px 0',
  keyRuleBulletMarginRight: 8,
  specialTopicPadding: 20,
  specialTopicBorderRadius: 8,
  specialTopicMarginBottom: 16,
  specialSubheadingMarginBottom: 10,
};

const FONT = {
  title: 28,
  titleMobile: 22,
  subtitle: 15,
  subtitleMobile: 14,
  sidebarTitle: 14,
  sidebarTitleWeight: 600,
  sidebarTitleLetterSpacing: 1,
  sidebarItem: 14,
  sectionHeading: 22,
  sectionHeadingWeight: 600,
  stepNumber: 18,
  stepNumberWeight: 700,
  stepName: 20,
  stepNameWeight: 600,
  stepDescription: 14,
  stepDescriptionLineHeight: 1.7,
  keyRuleText: 14,
  keyRuleLineHeight: 1.6,
  content: 14,
  contentLineHeight: 1.7,
  exampleLabel: 14,
  exampleLabelWeight: 600,
  exampleLabelLetterSpacing: 1,
  exampleScenario: 14,
  exampleScenarioWeight: 600,
  exampleResolution: 14,
  exampleResolutionLineHeight: 1.6,
  diagramItem: 14,
  diagramItemWeight: 600,
  diagramArrow: 16,
  introText: 14,
  introTextLineHeight: 1.7,
  backLink: 14,
  cardLink: 14,
  specialSubheading: 18,
  specialSubheadingWeight: 600,
  specialText: 14,
  specialTextLineHeight: 1.7,
};

const CARD_NAME_REGEX = /\b(Goblin Rabblemaster|Thalia, Heretic Cathar|Questing Beast|Robber of the Rich|Brave the Sands|Baneslayer Angel|Archon of Absolution|Wall of Runes|Icy Manipulator|Reconnaissance|Toski, Bearer of Secrets|Ohran Frostfang|Ankle Shanker|Goblin Chainwhirler|Kessig Wolf Run|Akroma, Angel of Wrath|Adorned Pouncer|Boros Charm|Mirran Crusader)\b/g;

const renderTextWithCardLinks = (text, colors) => {
  const parts = [];
  let lastIndex = 0;
  let match;

  const regex = new RegExp(CARD_NAME_REGEX.source, 'g');
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const cardName = match[0];
    parts.push(
      <Link
        key={`${cardName}-${match.index}`}
        to={`/cardsearch/${encodeURIComponent(cardName)}`}
        style={{
          color: colors.accentPrimary,
          backgroundColor: colors.accentBg,
          padding: SPACING.cardLinkPadding,
          borderRadius: SPACING.cardLinkBorderRadius,
          fontSize: FONT.cardLink,
          textDecoration: 'none',
        }}
      >
        {cardName}
      </Link>
    );
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
};

const SPECIAL_TOPICS = [
  {
    id: 'first-strike',
    title: 'First Strike & Double Strike',
    content:
      'When any creature in combat has first strike or double strike, the combat damage step is split into two sub-steps:\n\n' +
      '1. First-strike combat damage step: Only creatures with first strike or double strike assign and deal combat damage.\n' +
      '2. Regular combat damage step: All surviving creatures without first strike (plus double strike creatures again) assign and deal combat damage.\n\n' +
      'If a creature with first strike destroys its blocker in the first sub-step, the blocker deals no damage. Double strike creatures deal damage in both sub-steps, effectively hitting twice.',
    examples: [
      {
        scenario: 'A 3/3 creature with first strike is blocked by a 2/2 creature.',
        resolution: 'In the first-strike step, the 3/3 deals 3 damage to the 2/2, destroying it. The 2/2 never deals its combat damage because it is already destroyed before the regular damage step.',
      },
      {
        scenario: 'A 4/4 with double strike is blocked by a 5/5.',
        resolution: 'First-strike step: the 4/4 deals 4 damage to the 5/5. Regular step: the 4/4 deals 4 more damage (8 total, destroying the 5/5) and the 5/5 deals 5 damage back (destroying the 4/4). Both die.',
      },
    ],
  },
  {
    id: 'trample',
    title: 'Trample Damage Assignment',
    content:
      'A creature with trample can assign excess combat damage to the defending player, planeswalker, or battle it attacked, after assigning lethal damage to all of its blockers.\n\n' +
      'Lethal damage is the minimum damage needed to destroy the blocker (its toughness minus damage already marked on it). If a blocker has indestructible, the attacker only needs to assign damage equal to the blocker\'s toughness.\n\n' +
      'If a creature with trample is blocked but all its blockers are removed before combat damage, it assigns all its damage to the defending player.',
    examples: [
      {
        scenario: 'A 6/6 with trample is blocked by a 2/3.',
        resolution: 'The attacker assigns 3 damage (lethal) to the blocker and the remaining 3 damage tramples over to the defending player.',
      },
      {
        scenario: 'A 7/7 with trample is blocked by two creatures: a 1/1 and a 3/3.',
        resolution: 'The attacker assigns at least 1 to the 1/1 and at least 3 to the 3/3 (4 total lethal), then the remaining 3 tramples to the defending player.',
      },
    ],
  },
  {
    id: 'multiple-blockers',
    title: 'Multiple Blockers & Damage Assignment Order',
    content:
      'When a single attacking creature is blocked by multiple creatures, the attacking player chooses a damage assignment order for the blockers at the time blockers are declared. During the combat damage step, the attacker must assign lethal damage to the first blocker in the order before any damage can be assigned to the next blocker, and so on.\n\n' +
      'This order is locked in when blockers are declared and cannot be changed later, even if one of the blockers is removed from combat.',
    examples: [
      {
        scenario: 'A 5/5 creature is blocked by a 2/2, a 1/1, and a 3/3. The attacker orders them: 2/2 first, 3/3 second, 1/1 third.',
        resolution: 'The attacker must assign at least 2 damage to the 2/2, then at least 3 to the 3/3, leaving 0 for the 1/1. The 1/1 survives.',
      },
      {
        scenario: 'A 4/4 is blocked by two 3/3 creatures.',
        resolution: 'The attacker orders the blockers and assigns 3 damage to the first (killing it) and 1 damage to the second (which survives). Both blockers deal 3 damage to the attacker for 6 total, so the 4/4 dies.',
      },
    ],
  },
  {
    id: 'banding',
    title: 'Banding (Legacy Mechanic)',
    content:
      'Banding is an older mechanic that is no longer used on new cards but still functions in the rules.\n\n' +
      'When attacking, any number of creatures with banding (plus up to one without) can attack as a "band." They are treated as a unit for blocking purposes — if any creature in the band is blocked, the entire band is blocked.\n\n' +
      'The key benefit: the controller of a creature with banding chooses how combat damage is assigned to creatures in the band, instead of the opponent choosing. This allows you to distribute damage optimally among your creatures.\n\n' +
      'On defense, if a creature with banding blocks or is in a band that blocks, the defending player (not the attacker) assigns the attacker\'s combat damage among the blockers.',
    examples: [
      {
        scenario: 'Three creatures with banding attack together as a band and are blocked by a 6/6.',
        resolution: 'The controller of the banding creatures chooses how to distribute the 6 damage among their creatures, potentially saving the most important one.',
      },
    ],
  },
];

const SIDEBAR_ITEMS = [
  { id: 'intro', label: 'Introduction' },
  { id: 'timeline', label: 'Combat Flow' },
  ...COMBAT_STEPS.map((step, idx) => ({
    id: step.id,
    label: `${idx + 1}. ${step.name}`,
  })),
  { id: 'special-topics', label: 'Special Topics' },
  ...SPECIAL_TOPICS.map((topic) => ({
    id: topic.id,
    label: `↳ ${topic.title}`,
  })),
];

const CombatReference = () => {
  const { preferences } = usePreferences();
  const t = getThemeColors(preferences.theme);
  const [activeId, setActiveId] = useState('intro');

  useScrollToTop();

  const scrollToSection = useCallback((sectionId) => {
    setActiveId(sectionId);
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const renderExamples = (examples, keyPrefix) =>
    examples.map((ex, idx) => (
      <div
        id={`${keyPrefix}-ex-${idx}`}
        key={`${keyPrefix}-ex-${idx}`}
        style={{
          backgroundColor: t.backgroundSecondary,
          borderLeft: `${SPACING.exampleBorderLeft}px solid ${t.accentSecondary}`,
          borderRadius: SPACING.exampleBorderRadius,
          padding: SPACING.examplePadding,
          marginBottom: SPACING.exampleMarginBottom,
        }}
      >
        <div
          style={{
            fontSize: FONT.exampleLabel,
            fontWeight: FONT.exampleLabelWeight,
            color: t.accentPrimary,
            textTransform: 'uppercase',
            letterSpacing: FONT.exampleLabelLetterSpacing,
            marginBottom: SPACING.exampleLabelMarginBottom,
          }}
        >
          Example {examples.length > 1 ? idx + 1 : ''}
        </div>
        <div
          style={{
            fontSize: FONT.exampleScenario,
            fontWeight: FONT.exampleScenarioWeight,
            color: t.textPrimary,
            marginBottom: SPACING.exampleScenarioMarginBottom,
          }}
        >
          {renderTextWithCardLinks(ex.scenario, t)}
        </div>
        <div
          style={{
            fontSize: FONT.exampleResolution,
            color: t.textSecondary,
            lineHeight: FONT.exampleResolutionLineHeight,
          }}
        >
          {renderTextWithCardLinks(ex.resolution, t)}
        </div>
      </div>
    ));


  return (
    <>
      <NavigationBar />
      <div
        style={{
          maxWidth: SPACING.containerMaxWidth,
          margin: '0 auto',
          padding: isMobile ? SPACING.containerPaddingMobile : SPACING.containerPadding,
        }}
      >
        {/* Back link */}
        <div style={{ marginBottom: SPACING.backLinkMarginBottom }}>
          <Link to="/rules" style={{ color: t.accentPrimary, fontSize: FONT.backLink, textDecoration: 'none', display: 'inline-block', padding: SPACING.backLinkPadding }}>
            ← Back to Rules Reference
          </Link>
        </div>

        {/* Header */}
        <div style={{ marginTop: SPACING.headerMarginTop, marginBottom: SPACING.headerMarginBottom }}>
          <h1 style={{ fontSize: isMobile ? FONT.titleMobile : FONT.title, color: t.textPrimary, marginBottom: SPACING.titleMarginBottom }}>
            ⚔️ Combat
          </h1>
          <div style={{ fontSize: isMobile ? FONT.subtitleMobile : FONT.subtitle, color: t.textMuted }}>
            The five steps of the combat phase, timing, and damage assignment rules
          </div>
        </div>

        {/* Layout: sidebar + content */}
        <div style={{ display: 'flex', gap: isMobile ? 0 : SPACING.sidebarGap, alignItems: 'flex-start', flexDirection: isMobile ? 'column' : 'row' }}>
          {/* Sidebar Navigation - responsive layout handled by component */}
          <RulesSidebarNav
            sections={SIDEBAR_ITEMS}
            activeId={activeId}
            onSelect={scrollToSection}
          />

          {/* Main content */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Introduction */}
            <div id="intro" style={{ marginBottom: SPACING.sectionMarginBottom }}>
              <div
                style={{
                  backgroundColor: t.backgroundSecondary,
                  border: `1px solid ${t.borderPrimary}`,
                  borderRadius: SPACING.introBorderRadius,
                  padding: SPACING.introPadding,
                }}
              >
                <div style={{ fontSize: FONT.introText, color: t.textSecondary, lineHeight: FONT.introTextLineHeight }}>
                  <p style={{ marginBottom: SPACING.paragraphMarginBottom }}>
                    The <strong style={{ color: t.textPrimary }}>combat phase</strong> is where creatures attack and
                    block. It consists of five sequential steps, each with specific rules about what happens
                    automatically and when players can respond.
                  </p>
                  <p style={{ marginBottom: SPACING.paragraphMarginBottom }}>
                    Understanding the combat phase is essential for knowing <strong style={{ color: t.textPrimary }}>when</strong> to
                    cast combat tricks, removal spells, and activate abilities for maximum advantage.
                  </p>
                  <p style={{ margin: 0 }}>
                    Players receive priority during each step, allowing them to cast instants and activate abilities.
                    The key is knowing which step you are in and what information is available at that point.
                  </p>
                </div>
              </div>
            </div>

            {/* Combat Flow Timeline */}
            <div id="timeline" style={{ marginBottom: SPACING.sectionMarginBottom }}>
              <h2 style={{ fontSize: FONT.sectionHeading, fontWeight: FONT.sectionHeadingWeight, color: t.textPrimary, marginBottom: SPACING.panelMarginBottom }}>
                Combat Phase Flow
              </h2>
              <div
                style={{
                  backgroundColor: t.cardBackground,
                  border: `1px solid ${t.borderPrimary}`,
                  borderRadius: SPACING.diagramBorderRadius,
                  padding: SPACING.diagramPadding,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                {COMBAT_STEPS.map((step, idx) => (
                  <React.Fragment key={step.id}>
                    <div
                      onClick={() => scrollToSection(step.id)}
                      style={{
                        backgroundColor: t.accentBg,
                        border: `1px solid ${t.accentSecondary}`,
                        borderRadius: SPACING.diagramItemBorderRadius,
                        padding: SPACING.diagramItemPadding,
                        width: '100%',
                        maxWidth: 400,
                        textAlign: 'center',
                        cursor: 'pointer',
                        marginBottom: SPACING.diagramItemMarginBottom,
                      }}
                    >
                      <span style={{ fontSize: FONT.diagramItem, fontWeight: FONT.diagramItemWeight, color: t.textPrimary }}>
                        {idx + 1}. {step.name}
                      </span>
                    </div>
                    {idx < COMBAT_STEPS.length - 1 && (
                      <div style={{ fontSize: FONT.diagramArrow, color: t.textFaint, marginBottom: SPACING.diagramItemMarginBottom }}>
                        ▼
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* Combat Step Cards */}
            {COMBAT_STEPS.map((step, idx) => (
              <div
                key={step.id}
                id={step.id}
                style={{ marginBottom: SPACING.sectionMarginBottom }}
              >
                <Panel
                  bordered
                  style={{ backgroundColor: t.cardBackground, borderColor: t.borderPrimary }}
                >
                  {/* Step header */}
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: SPACING.panelMarginBottom }}>
                    <div
                      style={{
                        width: SPACING.stepNumberSize,
                        height: SPACING.stepNumberSize,
                        borderRadius: SPACING.stepNumberBorderRadius,
                        backgroundColor: t.accentBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: SPACING.stepNumberMarginRight,
                        flexShrink: 0,
                      }}
                    >
                      <span style={{ fontSize: FONT.stepNumber, fontWeight: FONT.stepNumberWeight, color: t.accentPrimary }}>
                        {idx + 1}
                      </span>
                    </div>
                    <h3 style={{ fontSize: FONT.stepName, fontWeight: FONT.stepNameWeight, color: t.textPrimary, margin: 0 }}>
                      {step.name}
                    </h3>
                  </div>

                  {/* Description */}
                  <div
                    style={{
                      fontSize: FONT.stepDescription,
                      color: t.textSecondary,
                      lineHeight: FONT.stepDescriptionLineHeight,
                      marginBottom: SPACING.panelMarginBottom,
                    }}
                  >
                    {renderTextWithCardLinks(step.description, t)}
                  </div>

                  {/* Key Rules */}
                  {step.keyRules && step.keyRules.length > 0 && (
                    <div style={{ marginBottom: SPACING.panelMarginBottom }}>
                      <h4 style={{ fontSize: FONT.content, fontWeight: FONT.exampleLabelWeight, color: t.accentPrimary, marginBottom: SPACING.exampleLabelMarginBottom }}>
                        Key Rules
                      </h4>
                      {step.keyRules.map((rule, rIdx) => (
                        <div
                          key={`${step.id}-rule-${rIdx}`}
                          style={{
                            display: 'flex',
                            padding: SPACING.keyRulePadding,
                            fontSize: FONT.keyRuleText,
                            color: t.textSecondary,
                            lineHeight: FONT.keyRuleLineHeight,
                          }}
                        >
                          <span style={{ color: t.accentPrimary, marginRight: SPACING.keyRuleBulletMarginRight, flexShrink: 0 }}>•</span>
                          <span>{renderTextWithCardLinks(rule, t)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Examples */}
                  {step.examples && step.examples.length > 0 && (
                    <div>
                      <div
                        style={{
                          fontSize: FONT.exampleLabel,
                          fontWeight: FONT.exampleLabelWeight,
                          color: t.accentPrimary,
                          textTransform: 'uppercase',
                          letterSpacing: FONT.exampleLabelLetterSpacing,
                          marginBottom: SPACING.exampleLabelMarginBottom,
                        }}
                      >
                        Examples
                      </div>
                      {renderExamples(step.examples, step.id)}
                    </div>
                  )}
                </Panel>
              </div>
            ))}

            {/* Special Topics */}
            <div id="special-topics" style={{ marginBottom: SPACING.sectionMarginBottom }}>
              <h2 style={{ fontSize: FONT.sectionHeading, fontWeight: FONT.sectionHeadingWeight, color: t.textPrimary, marginBottom: SPACING.panelMarginBottom }}>
                Special Topics
              </h2>

              {SPECIAL_TOPICS.map((topic) => (
                <div
                  key={topic.id}
                  id={topic.id}
                  style={{ marginBottom: SPACING.specialTopicMarginBottom }}
                >
                  <div
                    style={{
                      backgroundColor: t.cardBackground,
                      border: `1px solid ${t.borderPrimary}`,
                      borderRadius: SPACING.specialTopicBorderRadius,
                      padding: SPACING.specialTopicPadding,
                    }}
                  >
                    <h3 style={{ fontSize: FONT.specialSubheading, fontWeight: FONT.specialSubheadingWeight, color: t.textPrimary, marginBottom: SPACING.specialSubheadingMarginBottom }}>
                      {topic.title}
                    </h3>
                    <div
                      style={{
                        fontSize: FONT.specialText,
                        color: t.textSecondary,
                        lineHeight: FONT.specialTextLineHeight,
                        whiteSpace: 'pre-wrap',
                        marginBottom: topic.examples && topic.examples.length > 0 ? SPACING.panelMarginBottom : 0,
                      }}
                    >
                      {renderTextWithCardLinks(topic.content, t)}
                    </div>
                    {topic.examples && topic.examples.length > 0 && (
                      <div>
                        {renderExamples(topic.examples, topic.id)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <RulesDisclaimer />
      </div>
    </>
  );
};

export default CombatReference;
